import React, { useState, useContext, useEffect, useRef, useMemo } from 'react';
import apiClient from "../api/client";
import { MessageCircle, ArrowLeft, MapPin, Crown, Heart, Gift } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { fetchWhoLikedMe } from '../api/matches';
import { fetchEntitlements } from '../api/me';
import GiftHistoryTab from './GiftHistoryTab';
import { ProfileViewModal } from './profile/ProfileViewModal';
import { useProfileViewer } from '../hooks/useProfileViewer';
import { getAgeFromDob } from '../utils/ageCalculator';
import ProfileAvatar from './ProfileAvatar';
import { normalizeList } from '../utils/normalize';
import { safeArr, getDisplayName } from '../utils/safeProfile';


const Matches = () => {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // P2: Profile viewer modal
  const profileViewer = useProfileViewer();
  
  /**
   * Canonical profile view handler - fetches from /users/profile/{id}
   * Ensures consistent profile rendering across the entire app
   */
  const handleViewProfile = async (userId, fallbackData = {}) => {
    if (!userId || !token) return;
    
    try {
      const res = await apiClient.get(`/users/profile/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      profileViewer.showProfile(res.data, {
        isSelf: false,
        source: "matches:view",
      });
    } catch (e) {
      console.warn("[Matches] Failed to fetch profile, using fallback:", e);
      // Fallback to passed data if API fails
      profileViewer.showProfile({
        display_name: fallbackData.display_name || "User",
        ...fallbackData,
      }, { isSelf: false, source: "matches:fallback" });
    }
  };
  
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [likers, setLikers] = useState([]);
  const [loadingLikers, setLoadingLikers] = useState(false);
  const [isElite, setIsElite] = useState(false);
  
  // Ticket 20: Who Liked You feature
  const [activeTab, setActiveTab] = useState('matches');

  // Fetch entitlements to check Elite status
  useEffect(() => {
    fetchEntitlements()
      .then((ent) => setIsElite(ent.is_elite || false))
      .catch((err) => console.error('[Matches] Failed to fetch entitlements', err));
  }, []);

  // 🔧 FIX: Mark matches/likes as seen when viewing this page
  // Guard against double-clear in StrictMode
  const didClearRef = useRef({ matches: false, likes: false });
  
  useEffect(() => {
    const markSeen = async () => {
      if (!token) return;
      
      const type = activeTab === 'matches' ? 'matches' : 'likes';
      
      // Prevent duplicate clears in StrictMode
      if (didClearRef.current[type]) return;
      didClearRef.current[type] = true;
      
      try {
        const response = await apiClient.post(
          `/notifications/clear`,
          { type },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Use returned summary immediately (faster than re-fetch)
        if (response.data?.summary) {
          window.updateBadgeCounts?.(response.data.summary);
          window.updateDashboardBadges?.(response.data.summary);
        } else {
          window.refreshNotificationSummary?.();
        }
      } catch (err) {
        console.error('[Matches] Failed to mark as seen:', err);
      }
    };
    
    markSeen();
  }, [token, activeTab]);

  useEffect(() => {
    const fetchMatches = async () => {
      try {
        const response = await apiClient.get(`/matches`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Normalize to array in case API returns object/wrapper
        setMatches(normalizeList(response.data));
        
        // Clear matches notification badge
        try {
          await apiClient.post(`/notifications/clear`, 
            { type: 'matches' },
            { headers: { Authorization: `Bearer ${token}` }}
          );
        } catch (err) {
          console.error('[Matches] Failed to clear notification', err);
        }
      } catch (error) {
        console.error('Failed to fetch matches:', error);
        setMatches([]); // Reset to empty array on error
      } finally {
        setLoading(false);
      }
    };

    fetchMatches();
  }, [token]);

  // Ticket 20: Fetch who liked me when tab is active
  useEffect(() => {
    if (activeTab !== 'likers') return;
    
    setLoadingLikers(true);
    fetchWhoLikedMe()
      .then((data) => {
        // Normalize response to always be an array
        const normalized = normalizeList(data);
        setLikers(normalized);
        
        // Clear likes notification badge
        apiClient.post(`/notifications/clear`, 
          { type: 'likes' },
          { headers: { Authorization: `Bearer ${token}` }}
        ).catch((err) => console.error('[Matches] Failed to clear likes notification', err));
      })
      .catch((err) => {
        console.error('[Matches] Failed to fetch who liked me', err);
        setLikers([]); // Reset to empty array on error
      })
      .finally(() => setLoadingLikers(false));
  }, [activeTab, token]);

  // Memoize normalized likers list for safe mapping
  const likersList = useMemo(() => normalizeList(likers), [likers]);
  
  // Memoize normalized matches list for safe mapping
  const matchesList = useMemo(() => normalizeList(matches), [matches]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-warm">
        <div className="text-2xl font-playfair text-rose-600">Loading matches...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-warm">
      {/* Navigation */}
      <nav className="bg-white/90 backdrop-blur-sm border-b border-rose-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center space-x-2 text-gray-700 hover:text-rose-600"
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Dashboard</span>
            </button>
            
            <div className="flex items-center space-x-2">
              <MessageCircle className="w-6 h-6 text-rose-600" />
              <span className="font-playfair text-xl font-bold text-gray-900">
                Your Matches
              </span>
            </div>

            <div className="text-gray-600">
              {matchesList.length} match{matchesList.length !== 1 ? 'es' : ''}
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Ticket 20 & 26: Tab Switcher */}
        <div className="flex gap-2 mb-6 border-b border-rose-100">
          <button
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === 'matches'
                ? 'border-b-2 border-rose-500 text-rose-600'
                : 'text-gray-600 hover:text-rose-600'
            }`}
            onClick={() => setActiveTab('matches')}
          >
            <MessageCircle className="w-4 h-4 inline mr-1" />
            Matches
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium flex items-center gap-1 ${
              activeTab === 'likers'
                ? 'border-b-2 border-rose-500 text-rose-600'
                : 'text-gray-600 hover:text-rose-600'
            }`}
            onClick={() => setActiveTab('likers')}
          >
            <Heart className="w-4 h-4" />
            Who Liked You
            <Crown className="w-3 h-3 text-amber-500" />
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium flex items-center gap-1 ${
              activeTab === 'gifts'
                ? 'border-b-2 border-rose-500 text-rose-600'
                : 'text-gray-600 hover:text-rose-600'
            }`}
            onClick={() => setActiveTab('gifts')}
          >
            <Gift className="w-4 h-4" />
            Gifts Received
          </button>
        </div>

        {activeTab === 'matches' && matchesList.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {matchesList.map((match, index) => (
              <div
                key={match.match_id || index}
                className="glass rounded-2xl p-6 card-hover cursor-pointer"
                onClick={() => navigate(`/chat/${match.match_id}`)}
                data-testid={`match-card-${index}`}
              >
                {/* Profile Image */}
                <div className="h-48 bg-gradient-rose-gold rounded-xl mb-4 overflow-hidden relative flex items-center justify-center">
                  <ProfileAvatar 
                    profile={match.profile}
                    size={192}
                    className="!rounded-xl"
                    alt={getDisplayName(match.profile)}
                  />
                </div>

                {/* Match Info */}
                <div>
                  <h3 className="font-playfair text-xl font-bold text-gray-900 mb-2">
                    {getDisplayName(match.profile)}
                  </h3>
                  
                  <div className="flex items-center text-gray-600 mb-3">
                    <MapPin className="w-4 h-4 mr-1" />
                    <span className="text-sm">{match.profile.location}</span>
                  </div>

                  <div className="bg-rose-50 px-3 py-2 rounded-lg mb-4">
                    <span className="text-rose-700 text-sm font-medium">
                      {match.profile.background}
                    </span>
                  </div>

                  <p className="text-gray-700 text-sm mb-4 line-clamp-3">
                    {match.profile.bio}
                  </p>

                  {/* Interests */}
                  {safeArr(match.profile?.interests).length > 0 && (
                    <div className="mb-4">
                      <div className="flex flex-wrap gap-1">
                        {safeArr(match.profile?.interests).slice(0, 3).map((interest, idx) => (
                          <span 
                            key={idx} 
                            className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
                          >
                            {interest}
                          </span>
                        ))}
                        {safeArr(match.profile?.interests).length > 3 && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                            +{safeArr(match.profile?.interests).length - 3}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        // Use canonical profile viewer - same endpoint everywhere
                        handleViewProfile(match.profile.user_id, match.profile);
                      }}
                      className="flex-1 border-2 border-rose-600 text-rose-600 hover:bg-rose-50 py-2 text-sm rounded-lg font-semibold transition"
                    >
                      View Profile
                    </button>
                    <button className="flex-1 btn-primary py-2 text-sm">
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Message
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <MessageCircle className="w-12 h-12 text-rose-600" />
            </div>
            <h2 className="font-playfair text-3xl font-bold text-gray-900 mb-4">
              No Matches Yet
            </h2>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              Start discovering profiles and liking people you&apos;re interested in. 
              When they like you back, you&apos;ll see them here!
            </p>
            <button
              onClick={() => navigate('/discover')}
              className="btn-primary"
              data-testid="start-discovering-btn"
            >
              Start Discovering
            </button>
          </div>
        )}

        {/* Ticket 20: Who Liked You Tab */}
        {activeTab === 'likers' && (
          <div className="mt-2">
            {!isElite && (
              <div className="mb-4 rounded-xl border border-amber-500/60 bg-amber-500/10 p-4 text-sm">
                <div className="flex items-center gap-2 mb-2 text-amber-800 font-semibold">
                  <Crown className="w-5 h-5" />
                  <span>Upgrade to Elite</span>
                </div>
                <p className="text-amber-900 mb-3">
                  Unlock the ability to see exactly who liked you and flirted with you. Make connections faster!
                </p>
                <button
                  onClick={() => navigate('/profile/edit')}
                  className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-white font-semibold text-sm"
                >
                  View Elite Plans
                </button>
              </div>
            )}

            {loadingLikers && (
              <div className="text-center py-8 text-gray-600">Loading...</div>
            )}

            {!loadingLikers && likersList.length === 0 && (
              <div className="text-center py-8 text-gray-600">
                Nobody has liked you yet. Keep exploring! 💫
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {likersList.map((entry) => {
                const prof = entry.profile || {};
                const primaryPhoto = (prof.profile_photos || [])[0];

                const cardContent = (
                  <div className="relative rounded-xl surface-card border border-rose-100 dark:border-rose-900/30 overflow-hidden hover:shadow-lg transition-shadow">
                    <div className="aspect-[3/4] w-full bg-gray-100 flex items-center justify-center">
                      <ProfileAvatar 
                        profile={prof}
                        size={180}
                        className="!rounded-none !w-full !h-full"
                        alt={prof.display_name || 'User'}
                      />
                    </div>

                    <div className="p-3">
                      <div className="flex items-center justify-between gap-1 mb-1">
                        <span className="text-sm font-semibold text-gray-900 truncate">
                          {prof.display_name || 'Someone'}
                          {typeof prof.age === 'number' && `, ${prof.age}`}
                        </span>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            entry.type === 'flirt'
                              ? 'bg-pink-100 text-pink-600 border border-pink-200'
                              : 'bg-rose-100 text-rose-600 border border-rose-200'
                          }`}
                        >
                          {entry.type === 'flirt' ? '💌' : '❤️'}
                        </span>
                      </div>
                      {prof.location_city && (
                        <div className="flex items-center text-xs text-gray-500 truncate mb-2">
                          <MapPin className="w-3 h-3 mr-1" />
                          {prof.location_city}
                        </div>
                      )}
                      {/* View Profile button - only for Elite */}
                      {isElite && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Use canonical profile viewer - same endpoint everywhere
                            handleViewProfile(entry.user_id, entry);
                          }}
                          className="w-full mt-1 py-1.5 px-3 text-xs font-medium rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-100 border border-rose-200 transition-colors"
                        >
                          View Full Profile
                        </button>
                      )}
                    </div>
                  </div>
                );

                return (
                  <div key={`${entry.user_id}-${entry.type}-${entry.created_at}`}>
                    {isElite ? (
                      <button
                        type="button"
                        className="w-full text-left cursor-pointer"
                        onClick={() => {
                          // Use canonical profile viewer
                          handleViewProfile(entry.user_id, entry);
                        }}
                      >
                        {cardContent}
                      </button>
                    ) : (
                      <div className="relative">
                        <div className="blur-sm pointer-events-none">{cardContent}</div>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-xs px-3 py-1 rounded-full bg-black/70 text-amber-200 border border-amber-500/60 font-medium">
                            Elite Only
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}


        {/* Ticket 26: Gift History Tab */}
        {activeTab === 'gifts' && (
          <GiftHistoryTab />
        )}

      </div>

      {/* P2: Profile View Modal */}
      <ProfileViewModal
        open={profileViewer.open}
        onClose={profileViewer.close}
        profile={profileViewer.profile}
        isSelf={profileViewer.isSelf}
        source={profileViewer.source}
        onLikeFromProfile={profileViewer.onLikeFromProfile}
        onPassFromProfile={profileViewer.onPassFromProfile}
      />
    </div>
  );
};

export default Matches;